import { ErrorHandler, Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../environments/environment';

const CHUNK_RELOAD_STORAGE_KEY = 'chunk_reload_ts';
const CHUNK_RELOAD_COOLDOWN_MS = 60_000; // 60 seconds

function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const name = (error as { name?: string }).name;
  const message = (error as { message?: string }).message ?? '';
  return (
    name === 'ChunkLoadError' ||
    (typeof message === 'string' &&
      (message.includes('Loading chunk') || message.includes('Loading chunk failed')))
  );
}

@Injectable()
export class ChunkLoadErrorHandler extends ErrorHandler {
  constructor(@Inject(PLATFORM_ID) private platformId: object) {
    super();
  }

  override handleError(error: unknown): void {
    if (!isPlatformBrowser(this.platformId)) {
      super.handleError(error);
      return;
    }

    if (isChunkLoadError(error)) {
      const lastReload = getChunkReloadTimestamp();
      const now = Date.now();
      if (lastReload != null && now - lastReload < CHUNK_RELOAD_COOLDOWN_MS) {
        // Already reloaded recently; avoid infinite loop
        if (typeof (error as Error).message !== 'undefined') {
          console.error('ChunkLoadError (reload cooldown):', error);
        }
        throw error;
      }
      setChunkReloadTimestamp(now);
      if (!environment?.production) {
        console.warn('ChunkLoadError recovered, reloading', error);
      }
      window.location.reload();
      return;
    }

    throw error;
  }
}

function getChunkReloadTimestamp(): number | null {
  try {
    const raw = sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY);
    if (raw == null) return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function setChunkReloadTimestamp(ts: number): void {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(ts));
  } catch {
    // ignore
  }
}
